// @flow
import { intersection } from 'lodash';
import { ProductTransformations } from 'modules/productBrowser/transformation';
import { ProjectSelector } from 'modules/project/selector';
import type { AppState } from 'modules/rootReducer';
import type {
  ActiveProductFilters,
  ActiveProjectFilters,
  LocationOption,
  ProductFilters,
  ProjectFilters,
} from 'types/filter';
import type { Project } from 'types/project';

const getProductBrowserData = state => {
  return state.productBrowser.products;
};

const getProductBrowserLoading = state => {
  return state.productBrowser.loading.products;
};

const getProductBrowserError = state => {
  return state.productBrowser.error.products;
};

const getProductDetailsData = state => {
  return state.productBrowser.productDetails;
};

const getProductDetailsLoading = state => {
  return state.productBrowser.loading.productDetails;
};

const getProductDetailsError = state => {
  return state.productBrowser.error.productDetails;
};

const isLoading = state => {
  return getProductBrowserLoading(state) && getProductDetailsLoading(state);
};

const getProductsInProducts = state => {
  return ProductTransformations.displayProductsInProductBrowser(state);
};

const getFilteredProducts = (
  state: AppState,
  projectId: number | null,
  productsSelector
) => {
  let products = state.productBrowser.products;
  if (productsSelector) {
    products = productsSelector(state);
  }

  if (!products) {
    return [];
  }

  // project filter
  if (projectId) {
    products = products.filter(product => {
      const projectIds = product.detailCard.projects.map(project => project.id);

      return projectIds.indexOf(projectId) > -1;
    });
  }

  const activeFilters = getActiveProductFilters(state);
  if (!activeFilters) {
    return products;
  }

  // location filter
  if (activeFilters.location && activeFilters.location.length > 0) {
    products = products.filter(product => {
      const cityIds = product.detailCard.projects.map(
        project => project.cityId
      );

      return intersection(activeFilters.location, cityIds).length > 0;
    });
  }

  // property type filter
  if (activeFilters.propertyType && activeFilters.propertyType.length > 0) {
    products = products.filter(product => {
      return (
        activeFilters.propertyType.indexOf(product.detailCard.propertyType) > -1
      );
    });
  }

  // beds filter
  if (activeFilters.beds) {
    products = products.filter(product => {
      return product.detailCard.bedrooms >= activeFilters.beds;
    });
  }

  // area filter
  if (activeFilters.area && activeFilters.area.length > 0) {
    const [minArea, maxArea] = activeFilters.area;
    products = products.filter(product => {
      const productMinArea = parseFloat(
        product.detailCard.min_area.replace(',', '')
      );
      const productMaxArea = parseFloat(
        product.detailCard.max_area.replace(',', '')
      );
      return (
        (minArea <= productMinArea && productMinArea <= maxArea) ||
        (minArea <= productMaxArea && productMaxArea <= maxArea)
      );
    });
  }

  // projects filter
  if (activeFilters.projects && activeFilters.projects.length > 0) {
    products = products.filter(product => {
      const projectIds = product.detailCard.projects.map(project => project.id);

      return intersection(activeFilters.projects, projectIds).length > 0;
    });
  }

  return products;
};

const getProductFilters = (state: AppState): ProductFilters => {
  const filters = state.productBrowser.filters;
  if (!filters) {
    return null;
  }
  const projectFilters = ProjectSelector.getProjectFilters(state);
  if (!projectFilters) {
    return null;
  }

  const projects = ProjectSelector.getProjects(state) || { list: [] };

  return {
    location: projectFilters.location,
    propertyType: projectFilters.propertyType,
    beds: projectFilters.beds,
    area: filters.area,
    projects: projects.list.map((project: Project) => {
      return {
        id: project.id,
        title: project.name,
        subTitle: null,
        imageUrl: project.listImageUrl,
      };
    }),
  };
};

const getActiveProductFilters = (
  state: AppState
): Object<ActiveProductFilters> | null => {
  return state.productBrowser.activeFilters;
};

const getActiveProductFilterNames = (state: AppState): Array<string> => {
  const productFilters: ProductFilters = getProductFilters(state);
  const activeProductFilters: ActiveProductFilters = getActiveProductFilters(
    state
  );
  if (!productFilters || !activeProductFilters) {
    return [];
  }

  const filterNames = [];
  if (activeProductFilters.beds) {
    filterNames.push(`${activeProductFilters.beds}+ Beds`);
  }

  if (activeProductFilters.area && activeProductFilters.area.length > 0) {
    const [minArea, maxArea] = activeProductFilters.area;

    filterNames.push(
      `${minArea.toLocaleString()} - ${maxArea.toLocaleString()} Sq. Feet`
    );
  }

  if (activeProductFilters.propertyType.length > 0) {
    const propertyTypes = productFilters.propertyType
      .filter(type => {
        return activeProductFilters.propertyType.indexOf(type.id) > -1;
      })
      .map(type => type.title);
    filterNames.push(...propertyTypes);
  }

  if (activeProductFilters.location.length > 0) {
    const cities = productFilters.location.reduce(
      (activeCities: Array<string>, country: LocationOption) => {
        country.cities.forEach(city => {
          if (activeProductFilters.location.indexOf(city.id) > -1) {
            activeCities.push(city.name);
          }
        });

        return activeCities;
      },
      []
    );

    filterNames.push(...cities);
  }

  if (
    activeProductFilters.projects &&
    activeProductFilters.projects.length > 0
  ) {
    const projectNames = productFilters.projects
      .filter(project => activeProductFilters.projects.indexOf(project.id) > -1)
      .map(project => project.title);

    filterNames.push(...projectNames);
  }

  return filterNames;
};

const isProductsFiltered = (state: AppState): boolean => {
  const activeFilters: ActiveProductFilters = getActiveProductFilters(state);
  if (!activeFilters) {
    return false;
  }

  if (
    activeFilters.location.length > 0 ||
    activeFilters.propertyType.length > 0 ||
    activeFilters.beds ||
    activeFilters.area.length > 0 ||
    activeFilters.projects.length > 0
  ) {
    return true;
  }

  return false;
};

export const ProductBrowserSelector = {
  isLoading,
  getProductBrowserData,
  getProductBrowserLoading,
  getProductBrowserError,
  getProductDetailsData,
  getProductDetailsLoading,
  getProductDetailsError,
  getProductsInProducts,
  getFilteredProducts,
  getProductFilters,
  getActiveProductFilters,
  getActiveProductFilterNames,
  isProductsFiltered,
};
